import { importSPKI, jwtVerify } from 'jose';
import { isAxiosError } from 'axios';
import apiClient, { isHandledByErrorInterceptor } from '@/services/api/apiClient';
import { auth } from '@/config/firebase';

/**
 * MFA service — mirrors Flutter's IAuthRepository MFA methods.
 *
 * Handles email-based MFA — the only MFA type Flutter web uses (setMfaType(MFAType.email) in main.dart).
 * Phone MFA exists in Flutter but is unreachable, so it is not migrated.
 *
 * Flutter source files:
 *   - infrastructure/auth/i_auth_repository.dart — sendEmailVerificationOtp, verifyEmailOtp
 *   - domain/core/utils/jwt_utils.dart — JWT validation
 *   - domain/core/helpers/generic_helpers.dart — maskEmail
 */

// ── Types ────────────────────────────────────────────────────────

export interface SendOtpResult {
  success: boolean;
  /** Flutter's message for the failure (untranslated) */
  error?: string;
  /** 429 — Flutter shows the too-many-requests dialog, then logs out */
  rateLimited?: boolean;
  /** The apiClient interceptor already acted on it (401/403 logout, 409 duplicate account) */
  handledByInterceptor?: boolean;
}

export interface VerifyOtpResult {
  success: boolean;
  /** Flutter's message for the failure (untranslated) */
  error?: string;
  /** The apiClient interceptor already acted on it (401/403 logout, 409 duplicate account) */
  handledByInterceptor?: boolean;
}

// ── RSA Public Key for JWT Validation ────────────────────────────
// Mirrors Flutter JWTUtils.getPublicKey()

const RSA_PUBLIC_KEY_PEM = `-----BEGIN PUBLIC KEY-----
MIIBIjANBgkqhkiG9w0BAQEFAAOCAQ8AMIIBCgKCAQEA9CxkQ86J8i0qxMW7AcRu
xm/QbpAVDYdf4x/3wgKPI1eNSkK6gRrqLTgLEcOrHuZzgjVV5QgtjCTQLLssFrMy
kFIutZWktwTs5PmD8Ani5bpH4mRMOQjv90f8wHKOE6hc2hSG8ZP1CGWJUKbF8Jtt
CLB2JeS9i1fO9IoyQQGowQsKyC2fFEwKyIIk28ZRnaqcpz1PpKPvVmX86MQdLb/p
mKLqBjQSzS+MwGnLe2rydn3PrVRziYFoPSCM7MHLoiQJvTU1kqGvCXkQCjbbY62Q
pS0K+Fi8NVJyUijAmGBMoQR4z+Xo8GBieEWmNJMcG5ez3ZUw5PFmAjJRauhesHUp
WwIDAQAB
-----END PUBLIC KEY-----`;

// ── Error Messages ───────────────────────────────────────────────
// Flutter ErrorConstants / Constants texts (translation keys)

export const MFA_MESSAGES = {
  /** ErrorConstants.tooManyRequestsError */
  tooManyRequests: 'Too many requests. Please try again after some time.',
  /** Constants.failtToSendOTPMsg */
  failedToSendOtp: 'Failed to send OTP. Please check your internet connection and try again.',
  /** ErrorConstants.invalidOrExpiredEmailOtpMsg */
  invalidOrExpiredOtp: 'The OTP you entered is invalid or expired. Please try again.',
  /** ErrorConstants.genericError */
  genericError: 'Something went wrong, Please try again!',
  /** ErrorConstants.invalidCredentialError */
  invalidCredential: 'Invalid credentials. Please check your login information and try again.',
} as const;

const ERROR_MESSAGES = MFA_MESSAGES;

// ── Send Email OTP ───────────────────────────────────────────────

/**
 * Send email OTP for MFA verification.
 * Mirrors Flutter IAuthRepository.sendEmailVerificationOtp().
 *
 * API: POST /v1/otp/generate
 * Body: { primaryEmailId: email }
 */
export async function sendEmailOtp(email: string): Promise<SendOtpResult> {
  try {
    const response = await apiClient.post('v1/otp/generate', { primaryEmailId: email });
    if (response.status === 200) return { success: true };
    return { success: false, error: ERROR_MESSAGES.failedToSendOtp };
  } catch (error: unknown) {
    if (isHandledByErrorInterceptor(error)) {
      return { success: false, handledByInterceptor: true };
    }
    if (isAxiosError(error)) {
      if (String(error.response?.data).toLowerCase() === 'user unauthorized') {
        return { success: false, error: ERROR_MESSAGES.invalidCredential };
      }
      if (error.response?.status === 429) {
        return { success: false, rateLimited: true, error: ERROR_MESSAGES.tooManyRequests };
      }
    }
    return { success: false, error: ERROR_MESSAGES.failedToSendOtp };
  }
}

// ── Verify Email OTP ─────────────────────────────────────────────

/**
 * Verify email OTP for MFA.
 * Mirrors Flutter IAuthRepository.verifyEmailOtp().
 *
 * API: POST /v1/otp/validate
 * Body: { primaryEmailId: email, otp: otp }
 * Response: JWT token (validated using RSA256 public key)
 *
 * Flow:
 *   1. POST OTP to backend
 *   2. Backend returns JWT containing the OTP in payload
 *   3. Validate JWT signature using RSA256 public key
 *   4. Verify OTP in JWT matches submitted OTP
 *   5. Refresh Firebase token
 */
export async function verifyEmailOtp(
  email: string,
  otp: string,
): Promise<VerifyOtpResult> {
  try {
    const response = await apiClient.post('v1/otp/validate', {
      primaryEmailId: email,
      otp,
    });
    if (response.status !== 200) {
      return { success: false, error: ERROR_MESSAGES.invalidOrExpiredOtp };
    }

    let jwtToken: string | null = null;
    if (typeof response.data === 'string') {
      jwtToken = response.data.trim();
    } else if (typeof response.data === 'object' && response.data !== null) {
      const data = response.data as Record<string, unknown>;
      const tokenValue = data['token'] ?? data['jwt'] ?? data['data'] ?? data['access_token'];
      if (typeof tokenValue === 'string') {
        jwtToken = tokenValue.trim();
      }
    }

    // Refresh Firebase ID token after OTP validation
    try {
      await auth.currentUser?.getIdToken(true);
    } catch {
      // non-critical
    }

    return authenticateJWT(jwtToken, otp);
  } catch (error: unknown) {
    if (isHandledByErrorInterceptor(error)) {
      return { success: false, handledByInterceptor: true };
    }
    // Every error response, 429 included, is "invalid or expired": Dio throws for non-2xx, so
    // Flutter's statusCode == 429 branch is never reached and its catch returns this message.
    return { success: false, error: ERROR_MESSAGES.invalidOrExpiredOtp };
  }
}

// ── JWT Validation ───────────────────────────────────────────────

/**
 * Validate JWT token and verify OTP match.
 * Mirrors Flutter IAuthRepository._authenticateJWT() + JWTUtils.validateJWTAndExtractOTP().
 *
 * Uses the `jose` library (imported in package.json) for RSA256 JWT verification.
 */
async function authenticateJWT(
  jwtToken: string | null,
  expectedOtp: string,
): Promise<VerifyOtpResult> {
  try {
    if (!jwtToken || jwtToken.length === 0) {
      return { success: false, error: ERROR_MESSAGES.genericError };
    }

    // Validate JWT format (must have 3 parts)
    const parts = jwtToken.split('.');
    if (parts.length !== 3) {
      return { success: false, error: ERROR_MESSAGES.genericError };
    }

    // Import the RSA public key and verify the JWT
    const publicKey = await importSPKI(RSA_PUBLIC_KEY_PEM, 'RS256');
    const { payload } = await jwtVerify(jwtToken, publicKey);

    // Extract OTP from JWT payload and compare
    const jwtOtp = payload['otp'] != null ? String(payload['otp']) : null;
    const isOtpMatch = jwtOtp !== null && jwtOtp === expectedOtp;

    if (isOtpMatch) {
      return { success: true };
    }

    return { success: false, error: ERROR_MESSAGES.genericError };
  } catch {
    return { success: false, error: ERROR_MESSAGES.genericError };
  }
}

// ── Helpers ──────────────────────────────────────────────────────

/**
 * Mask email for display.
 * Mirrors Flutter GenericHelpers.maskEmail().
 * Example: "john.doe@example.com" → "jo****e@example.com"; usernames of 2 chars or fewer are not masked.
 */
export function maskEmail(email: string): string {
  const parts = email.split('@');
  if (parts.length !== 2) return email;
  const [username, domain] = parts;
  if (username.length <= 2) return email;
  return `${username.substring(0, 2)}****${username.substring(username.length - 1)}@${domain}`;
}
